import { useState } from 'react';
import { api } from '@/api/client';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';

interface ExportButtonProps {
  siteKey: string;
  from: string;
  to: string;
  /** Omit to export every breakdown — one sheet each — rather than a single table. */
  dimension?: string;
  filterDimension?: string;
  filterValue?: string;
  label?: string;
}

/**
 * Downloads the current view as a spreadsheet.
 *
 * Takes the SAME range and filter the page is showing. An export button that quietly exports
 * something else — the default range, or the unfiltered set — is the kind of bug nobody catches
 * until a number in a meeting disagrees with the screen it came from.
 *
 * Hidden entirely without the export permission, rather than shown and 403-ing: a control that
 * cannot work is worse than no control.
 */
export function ExportButton({
  siteKey, from, to, dimension, filterDimension, filterValue, label = 'Export Excel',
}: ExportButtonProps) {
  const [state, setState] = useState<'idle' | 'working' | 'error'>('idle');
  const [message, setMessage] = useState<string>();

  if (!hasPermission(PERMISSIONS.analyticsExport)) return null;

  const run = async () => {
    setState('working');
    setMessage(undefined);
    try {
      await api.exportAnalytics(siteKey, { dimension, filterDimension, filterValue, from, to });
      setState('idle');
    } catch (error) {
      setState('error');
      setMessage(error instanceof Error ? error.message : 'Export failed');
    }
  };

  return (
    <span className="export-button">
      <button className="btn btn-ghost" onClick={run} disabled={state === 'working'}>
        {state === 'working' ? 'Preparing…' : label}
      </button>
      {/* The range is stated next to the button — the file is named for it, and a reader who
          exports the wrong period will not discover it until the file is already circulating. */}
      <span className="t-label subtle">{from} → {to}</span>
      {state === 'error' && <span className="t-label note-error">{message}</span>}
    </span>
  );
}
